import { differenceInDays, format, isAfter } from 'date-fns';

import type { DefaultTFuncReturn } from 'i18next';
import type { UserAssignment } from '@/client/types/dashboard/UserAssignment';
import { useTranslation } from 'react-i18next';

export const useSetCompletionStatus = () => {
  const { t } = useTranslation();

  const setCompletionStatus = (assignment: UserAssignment) => {
    const completedDate = assignment.completed_date;
    const dueDate = assignment.due_date;

    const isCompleted = !!completedDate;
    const isOverdue =
      dueDate && isAfter(new Date().setHours(0, 0, 0, 0), new Date(dueDate).setHours(0, 0, 0, 0));
    const isCompletedOverdue = isCompleted && isOverdue;
    const isFuture =
      dueDate && isAfter(new Date(dueDate).setHours(0, 0, 0, 0), new Date().setHours(0, 0, 0, 0));

    let statusText: DefaultTFuncReturn | undefined;

    let status: 'completed' | 'completedOverdue' | 'overdue' | 'notStarted' | undefined;

    if (isCompletedOverdue) {
      statusText = t('learners.dashboard.assignments.status.completedOverdue', {
        date: format(new Date(completedDate!), 'MMM d, yyyy'),
      });

      status = 'completedOverdue';
    } else if (isCompleted) {
      statusText = t('learners.dashboard.assignments.status.completedOnTime', {
        date: format(new Date(completedDate!), 'MMM d, yyyy'),
      });

      status = 'completed';
    } else if (isOverdue) {
      const daysOverdue = Math.ceil(
        differenceInDays(new Date().setHours(0, 0, 0, 0), new Date(dueDate!).setHours(0, 0, 0, 0)),
      );
      statusText = t('learners.dashboard.assignments.status.daysOverdue', {
        days: daysOverdue.toLocaleString(),
      });

      status = 'overdue';
    } else if (isFuture) {
      const daysUntilDue = Math.ceil(
        differenceInDays(new Date(dueDate!).setHours(0, 0, 0, 0), new Date().setHours(0, 0, 0, 0)),
      );

      statusText = t('learners.dashboard.assignments.status.dueIn', {
        days: daysUntilDue.toLocaleString(),
      });

      status = 'notStarted';
    }

    return { statusText, status };
  };

  return { setCompletionStatus };
};
